import React, { useState } from 'react';
import axios from 'axios';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useAuth } from '../auth/AuthContext';

const parseApiError = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string; errors?: string[] } | undefined;
    const errors = data?.errors;
    if (Array.isArray(errors) && errors.length > 0) {
      return errors.join(', ');
    }
    const errorMessage = typeof data?.error === 'string' ? data.error.trim() : '';
    if (errorMessage) {
      return errorMessage;
    }
  }
  return 'Failed to change password';
};

const ChangePasswordPage: React.FC = () => {
  const { changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (newPassword !== newPasswordConfirmation) {
      setError('New password confirmation does not match');
      return;
    }

    setSaving(true);
    try {
      await changePassword(currentPassword, newPassword, newPasswordConfirmation);
      setSuccess('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirmation('');
    } catch (err: unknown) {
      setError(parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
      <Paper sx={{ width: 520, maxWidth: '100%', p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Change password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Re-enter your current password and choose a new strong password.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Current password"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
          <TextField
            label="New password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
            required
          />
          <TextField
            label="Confirm new password"
            type="password"
            value={newPasswordConfirmation}
            onChange={(event) => setNewPasswordConfirmation(event.target.value)}
            autoComplete="new-password"
            required
          />
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? 'Updating...' : 'Update password'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ChangePasswordPage;
